/**
 * Webhook store - manages webhooks and deliveries
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  webhooksApi,
  type Webhook,
  type WebhookWithSecret,
  type WebhookDelivery,
  type CreateWebhookRequest,
  type UpdateWebhookRequest,
  type WebhookEventType,
} from '@services/api';

interface WebhookState {
  // Webhooks
  webhooks: Webhook[];
  webhooksLoading: boolean;
  webhooksError: string | null;
  webhooksTotal: number;

  // Selected webhook
  selectedWebhook: Webhook | null;
  selectedWebhookSecret: string | null;

  // Event types
  eventTypes: WebhookEventType[];
  eventTypesLoading: boolean;

  // Deliveries for selected webhook
  deliveries: WebhookDelivery[];
  deliveriesLoading: boolean;
  deliveriesTotal: number;

  // Selected delivery
  selectedDelivery: WebhookDelivery | null;

  // Operations in progress
  creatingWebhook: boolean;
  testingWebhook: string | null; // webhookId being tested
  retryingDelivery: string | null; // deliveryId being retried

  // Actions
  fetchEventTypes: () => Promise<void>;
  fetchWebhooks: (organizationId: string) => Promise<void>;
  createWebhook: (organizationId: string, data: CreateWebhookRequest) => Promise<WebhookWithSecret>;
  updateWebhook: (organizationId: string, webhookId: string, data: UpdateWebhookRequest) => Promise<void>;
  deleteWebhook: (organizationId: string, webhookId: string) => Promise<void>;
  toggleWebhook: (organizationId: string, webhookId: string) => Promise<void>;
  regenerateSecret: (organizationId: string, webhookId: string) => Promise<string>;
  testWebhook: (organizationId: string, webhookId: string) => Promise<WebhookDelivery>;
  fetchDeliveries: (organizationId: string, webhookId: string) => Promise<void>;
  retryDelivery: (organizationId: string, deliveryId: string) => Promise<void>;
  selectWebhook: (webhook: Webhook | null) => void;
  selectDelivery: (delivery: WebhookDelivery | null) => void;
  clearSecret: () => void;
  reset: () => void;
}

const initialState = {
  webhooks: [],
  webhooksLoading: false,
  webhooksError: null,
  webhooksTotal: 0,
  selectedWebhook: null,
  selectedWebhookSecret: null,
  eventTypes: [],
  eventTypesLoading: false,
  deliveries: [],
  deliveriesLoading: false,
  deliveriesTotal: 0,
  selectedDelivery: null,
  creatingWebhook: false,
  testingWebhook: null,
  retryingDelivery: null,
};

export const useWebhookStore = create<WebhookState>()(
  immer((set, get) => ({
    ...initialState,

    // =========================================================================
    // Event Types
    // =========================================================================

    fetchEventTypes: async () => {
      if (get().eventTypes.length > 0) return; // Already loaded

      set((state) => {
        state.eventTypesLoading = true;
      });

      try {
        const types = await webhooksApi.listEventTypes();
        set((state) => {
          state.eventTypes = types;
          state.eventTypesLoading = false;
        });
      } catch (error) {
        set((state) => {
          state.eventTypesLoading = false;
        });
      }
    },

    // =========================================================================
    // Webhooks CRUD
    // =========================================================================

    fetchWebhooks: async (organizationId) => {
      set((state) => {
        state.webhooksLoading = true;
        state.webhooksError = null;
      });

      try {
        const response = await webhooksApi.listWebhooks(organizationId);
        set((state) => {
          state.webhooks = response.webhooks;
          state.webhooksTotal = response.total;
          state.webhooksLoading = false;
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to load webhooks';
        set((state) => {
          state.webhooksLoading = false;
          state.webhooksError = message;
        });
      }
    },

    createWebhook: async (organizationId, data) => {
      set((state) => {
        state.creatingWebhook = true;
      });

      try {
        const webhook = await webhooksApi.createWebhook(organizationId, data);
        set((state) => {
          state.webhooks.unshift(webhook);
          state.webhooksTotal += 1;
          state.creatingWebhook = false;
          state.selectedWebhook = webhook;
          state.selectedWebhookSecret = webhook.secret;
        });
        return webhook;
      } catch (error) {
        set((state) => {
          state.creatingWebhook = false;
        });
        throw error;
      }
    },

    updateWebhook: async (organizationId, webhookId, data) => {
      const webhook = await webhooksApi.updateWebhook(organizationId, webhookId, data);
      set((state) => {
        const idx = state.webhooks.findIndex((w) => w.id === webhookId);
        if (idx !== -1) {
          state.webhooks[idx] = webhook;
        }
        if (state.selectedWebhook?.id === webhookId) {
          state.selectedWebhook = webhook;
        }
      });
    },

    deleteWebhook: async (organizationId, webhookId) => {
      await webhooksApi.deleteWebhook(organizationId, webhookId);
      set((state) => {
        state.webhooks = state.webhooks.filter((w) => w.id !== webhookId);
        state.webhooksTotal -= 1;
        if (state.selectedWebhook?.id === webhookId) {
          state.selectedWebhook = null;
        }
      });
    },

    toggleWebhook: async (organizationId, webhookId) => {
      const webhook = get().webhooks.find((w) => w.id === webhookId);
      if (!webhook) return;

      await get().updateWebhook(organizationId, webhookId, {
        enabled: !webhook.enabled,
      });
    },

    regenerateSecret: async (organizationId, webhookId) => {
      const webhook = await webhooksApi.regenerateSecret(organizationId, webhookId);
      set((state) => {
        const idx = state.webhooks.findIndex((w) => w.id === webhookId);
        if (idx !== -1) {
          state.webhooks[idx] = webhook;
        }
        if (state.selectedWebhook?.id === webhookId) {
          state.selectedWebhook = webhook;
          state.selectedWebhookSecret = webhook.secret;
        }
      });
      return webhook.secret;
    },

    testWebhook: async (organizationId, webhookId) => {
      set((state) => {
        state.testingWebhook = webhookId;
      });

      try {
        const delivery = await webhooksApi.testWebhook(organizationId, webhookId);
        set((state) => {
          state.testingWebhook = null;
          // Prepend to deliveries if viewing this webhook
          if (state.selectedWebhook?.id === webhookId) {
            state.deliveries.unshift(delivery);
            state.deliveriesTotal += 1;
          }
        });
        return delivery;
      } catch (error) {
        set((state) => {
          state.testingWebhook = null;
        });
        throw error;
      }
    },

    // =========================================================================
    // Deliveries
    // =========================================================================

    fetchDeliveries: async (organizationId, webhookId) => {
      set((state) => {
        state.deliveriesLoading = true;
      });

      try {
        const response = await webhooksApi.listDeliveries(organizationId, webhookId);
        set((state) => {
          state.deliveries = response.deliveries;
          state.deliveriesTotal = response.total;
          state.deliveriesLoading = false;
        });
      } catch (error) {
        set((state) => {
          state.deliveriesLoading = false;
        });
      }
    },

    retryDelivery: async (organizationId, deliveryId) => {
      set((state) => {
        state.retryingDelivery = deliveryId;
      });

      try {
        const delivery = await webhooksApi.retryDelivery(organizationId, deliveryId);
        set((state) => {
          const idx = state.deliveries.findIndex((d) => d.id === deliveryId);
          if (idx !== -1) {
            state.deliveries[idx] = delivery;
          }
          state.retryingDelivery = null;
        });
      } catch (error) {
        set((state) => {
          state.retryingDelivery = null;
        });
        throw error;
      }
    },

    // =========================================================================
    // Selection
    // =========================================================================

    selectWebhook: (webhook) => {
      set((state) => {
        state.selectedWebhook = webhook;
        state.selectedWebhookSecret = null;
        state.deliveries = [];
        state.deliveriesTotal = 0;
        state.selectedDelivery = null;
      });
    },

    selectDelivery: (delivery) => {
      set((state) => {
        state.selectedDelivery = delivery;
      });
    },

    clearSecret: () => {
      set((state) => {
        state.selectedWebhookSecret = null;
      });
    },

    reset: () => {
      set(initialState);
    },
  }))
);

export default useWebhookStore;
